import React, { useEffect, useRef, useState } from 'react';

const NO_HIGHLIGHTED_OPTION = -1;
const FIRST_OPTION_INDEX = 0;
const NEXT_OPTION = 1;
const PREVIOUS_OPTION = -1;

export interface IComboBoxProps {
    name: string;
    options: any[];
    placeholder?: string;
    defaultValue?: string;
    optionModifier?: string;
    onSelect?: (evt: any) => void;
    selectedOption: string;
    multiselectable?: boolean;
    selectedOptions?: Array<string>;
    disabled?: boolean;
    isError?: boolean;
}
export interface IComboBoxOptionProps {
    parentName: string;
    optionName: string;
    index: number;
    onSelect: any;
    selected: boolean;
    optionLabel: string;
    ariaActive?: boolean;
}

export const ComboBoxOption: React.FC<IComboBoxOptionProps> = ({
    parentName,
    optionName,
    index,
    onSelect,
    selected,
    optionLabel,
    ariaActive = false,
}) => {
    return (
        <div
            role='option'
            id={`${parentName}-option-${index}`}
            className={`c-combobox__item combobox-item${
                ariaActive ? ' is-highlighted' : ''
            }`}
            data-value={`${optionName.replace(/ /, '_').toLowerCase()}`}
            aria-selected={selected}
            aria-disabled='false'
            onClick={() => onSelect(optionName)}
        >
            <div className='c-combobox__item__text'>{optionLabel}</div>
        </div>
    );
};

export const ComboBox: React.FC<IComboBoxProps> = ({
    name,
    options,
    placeholder = '',
    defaultValue = '',
    onSelect,
    selectedOption,
    optionModifier,
    multiselectable = false,
    selectedOptions,
    disabled = false,
    isError = false,
}) => {
    const [visible, setVisible] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(
        NO_HIGHLIGHTED_OPTION,
    );
    const listboxRef = useRef<HTMLDivElement>(null);
    const disabledClass = disabled ? 'disabled' : '';

    useEffect(() => {
        if (!visible || highlightedIndex < FIRST_OPTION_INDEX) return;

        listboxRef.current
            ?.querySelector<HTMLElement>('.is-highlighted')
            ?.scrollIntoView?.({ block: 'nearest' });
    }, [highlightedIndex, options, visible]);

    const handleClick = () => {
        if (!disabled) {
            setVisible(!visible);
            setHighlightedIndex(
                options.findIndex((option) => option.value === selectedOption),
            );
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled || !options.length) return;

        if (event.key === 'Escape') {
            event.preventDefault();
            setVisible(false);
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (!visible) {
                setVisible(true);
                setHighlightedIndex(
                    options.findIndex(
                        (option) => option.value === selectedOption,
                    ),
                );
            } else if (highlightedIndex >= FIRST_OPTION_INDEX) {
                onSelect?.(options[highlightedIndex].value);
                setVisible(false);
            }
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const direction =
                event.key === 'ArrowDown' ? NEXT_OPTION : PREVIOUS_OPTION;
            const currentIndex = highlightedIndex;
            const nextIndex =
                (currentIndex + direction + options.length) % options.length;
            setHighlightedIndex(nextIndex);
            setVisible(true);
        }
    };

    const displayedValue = (): string => {
        if (!selectedOption && (!!placeholder || !!defaultValue)) {
            return placeholder || defaultValue;
        } else if (!!optionModifier) {
            return `${
                options.find((option) => option.value == selectedOption).label
            } ${optionModifier}`;
        } else {
            return (
                options.find((option) => option.value == selectedOption) || {
                    value: '',
                    label: '',
                }
            ).label;
        }
    };

    return (
        <div className='c-combobox__wrapper  js-combobox'>
            <input
                type='hidden'
                name='combobox-values'
                className='js-combobox__input-values'
                disabled={disabled}
            ></input>
            <div
                role='combobox'
                aria-haspopup='listbox'
                aria-expanded={visible}
                aria-owns={`${name}-listbox`}
                aria-controls={`${name}-listbox`}
                aria-activedescendant={
                    highlightedIndex >= FIRST_OPTION_INDEX
                        ? `${name}-option-${highlightedIndex}`
                        : undefined
                }
                tabIndex={0}
                id={name}
                className={`c-combobox ${isError ? 'isError' : ''}`}
                aria-disabled={disabled}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                onBlur={() => setVisible(false)}
            >
                <div className='c-combobox__value-presentation  js-combobox-value-presentation'>
                    <span
                        id={`${name}-values`}
                        className={
                            'c-combobox__value-presentation__text  has-placeholder' +
                            disabledClass
                        }
                    >
                        {displayedValue()}
                    </span>
                </div>

                <div className='c-combobox__listbox-container'>
                    <div
                        id={`${name}-listbox`}
                        className='c-combobox__listbox'
                        role='listbox'
                        aria-multiselectable={`${multiselectable}`}
                        aria-disabled='false'
                        ref={listboxRef}
                    >
                        {options.map((option, index) => {
                            const selectedOp =
                                multiselectable && selectedOptions
                                    ? selectedOptions.includes(option)
                                    : selectedOption === option;
                            return (
                                <ComboBoxOption
                                    key={`${option.value}-${Math.random()}`}
                                    parentName={name}
                                    optionName={option.value}
                                    index={index}
                                    onSelect={onSelect}
                                    selected={selectedOp}
                                    optionLabel={option.label}
                                    ariaActive={highlightedIndex === index}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
